import { CRMChannel } from "@jugl-web/rest-api";
import { Tooltip, DataLoadingWrapper } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { FC, ReactNode, useState } from "react";
import {
  useAgentAnalyticsContext,
  useChatsOverviewChannelSummaries,
  useChatsOverviewKPICards,
  useChatsOverviewKPIData,
} from "@jugl-web/domain-resources/analytics";
import { KPICard } from "@jugl-web/domain-resources/analytics/components/KPICard";
import { ChatsByCountriesWidget } from "../ChatsByCountriesWidget";
import aiAgentProfilePic from "../../assets/ai-agent-profile.png";
import { ReactComponent as ChannelsIcon } from "../../assets/channels.svg";
import humanProfilePic from "../../assets/human-profile.png";
import { ReactComponent as InfoIcon } from "../../assets/info.svg";
import { AnalyticsPageHeader } from "../AnalyticsPageHeader";
import { ChannelCard } from "../ChannelCard";
import { SummaryCard } from "../SummaryCard";

export const ChatsOverviewTabContent: FC = () => {
  const { entityId } = useEntitySelectedProvider();
  const [selectedChannel, setSelectedChannel] = useState<CRMChannel>();

  const {
    chatsByCountriesData,
    isLoading: isAnalyticsLoading,
    isError: isAnalyticsError,
    refetch: analyticsRefetch,
  } = useAgentAnalyticsContext();

  const { t } = useTranslations();

  const {
    totals,
    insightsByChannel,
    isLoading: isInsightsLoading,
    isError: isInsightsError,
    refetch: insightsRefetch,
  } = useChatsOverviewKPIData(entityId);

  const kpiCards = useChatsOverviewKPICards(totals);
  const channelSummaries = useChatsOverviewChannelSummaries(insightsByChannel);

  const selectedSummary = selectedChannel
    ? channelSummaries.find((s) => s.channel === selectedChannel)
    : undefined;

  return (
    <DataLoadingWrapper
      isLoading={isInsightsLoading || isAnalyticsLoading}
      isError={isInsightsError}
      onRetry={() => {
        if (isInsightsError) insightsRefetch();
        if (isAnalyticsError) analyticsRefetch();
      }}
    >
      {(() => {
        if (!totals) {
          return null;
        }

        return (
          <div className="mx-auto flex w-full flex-col gap-10 p-12">
            {/* Header */}
            <AnalyticsPageHeader
              title={t({
                id: "cx-analytics.chats-overview",
                defaultMessage: "Chats Overview",
              })}
              subtitle={t({
                id: "cx-analytics.chats-overview-tab-subtitle",
                defaultMessage:
                  "General highlights based on chats from the current month",
              })}
              hints={[
                t({
                  id: "cx-analytics.chats-overview-hint-1",
                  defaultMessage:
                    "This dashboard shows an overview of your customer chats across all channels 📊",
                }),
                t({
                  id: "cx-analytics.chats-overview-hint-2",
                  defaultMessage:
                    "Track total chats, messages, customer locations, and how many were handled by AI vs humans ⚡",
                }),
                t({
                  id: "cx-analytics.chats-overview-hint-3",
                  defaultMessage:
                    "Use this data to monitor performance and improve your communication strategy 📈",
                }),
              ]}
            />

            {/* KPI Cards */}
            <div className="flex gap-6">
              {kpiCards.map((card) => (
                <KPICard key={card.id} {...card} />
              ))}
            </div>

            {/* Chats by Channels */}
            <div className="mt-4 flex flex-col gap-6">
              <div className="flex items-center gap-2">
                <ChannelsIcon className="h-5 w-5 shrink-0" />
                <span className="font-secondary text-sm font-medium text-[#40494D]">
                  {t({
                    id: "cx-analytics.chats-by-channels",
                    defaultMessage: "Chats by channels",
                  })}
                </span>
              </div>
              <div className="flex gap-[76px]">
                {/* Left: Channel cards */}
                <div className="flex flex-1 flex-col gap-6">
                  {channelSummaries.map((ch, idx) => (
                    <ChannelCard
                      key={ch.channel}
                      icon={ch.icon}
                      name={ch.name}
                      index={idx + 1}
                      chatsCount={ch.totalChats}
                      messages={ch.totalMessages}
                      isSelected={selectedChannel === ch.channel}
                      onMouseEnter={() => setSelectedChannel(ch.channel)}
                      onMouseLeave={() => setSelectedChannel(undefined)}
                    />
                  ))}
                </div>

                {/* Right: Summary cards */}
                <div className="flex flex-1 flex-col gap-6">
                  <SummaryCard
                    icon={
                      <img
                        src={aiAgentProfilePic}
                        alt=""
                        className="h-12 w-12 shrink-0 rounded-full border border-solid border-[#D2D2D8] object-cover"
                      />
                    }
                    label={t({
                      id: "cx-analytics.managed-by-ai-agent",
                      defaultMessage: "Managed by AI Agent",
                    })}
                    chatsCount={
                      selectedSummary
                        ? selectedSummary.aiAgentChats
                        : totals.totalAiConversations
                    }
                    messages={
                      selectedSummary
                        ? selectedSummary.aiAgentMessages
                        : totals.aiReplies
                    }
                  />
                  <SummaryCard
                    icon={
                      <img
                        src={humanProfilePic}
                        alt=""
                        className="h-12 w-12 shrink-0 rounded-full border border-solid border-[#D2D2D8] bg-[#DEDEE4] object-cover"
                      />
                    }
                    label={t({
                      id: "cx-analytics.transferred-to-human",
                      defaultMessage: "Transferred to Human",
                    })}
                    chatsCount={
                      selectedSummary
                        ? selectedSummary.humanChats
                        : totals.unresolved
                    }
                    messages={
                      selectedSummary
                        ? selectedSummary.humanMessages
                        : totals.humanSentMessages
                    }
                    infoIconSlot={
                      totals.missedAiReply > 0 ? (
                        <Tooltip
                          placement="bottom"
                          delay="none"
                          isMouseOnly={false}
                          className="text-center"
                          renderTrigger={({ ref, props }) => (
                            <div
                              ref={ref}
                              className="flex h-8 w-8 items-center justify-center rounded-full bg-grey-100 transition-colors hover:bg-grey-200"
                              {...props}
                            >
                              <InfoIcon />
                            </div>
                          )}
                        >
                          {t(
                            {
                              id: "cx-analytics.ai-automation-leakage-tooltip",
                              defaultMessage:
                                "<b>{count} {count, plural,one {chat} other {chats}}</b> across all the channels {count, plural, one {was} other {were}} automatically escalated to a human agent because AI <u>Message Credits</u> ran out",
                            },
                            {
                              count: totals.missedAiReply,
                              b: (chunks: (ReactNode | string)[]) => (
                                <b>{chunks}</b>
                              ),
                              u: (chunks: (ReactNode | string)[]) => (
                                <u>{chunks}</u>
                              ),
                            }
                          )}
                        </Tooltip>
                      ) : undefined
                    }
                  />
                </div>
              </div>
            </div>

            {/* Chats by Countries widget */}
            <div className="mt-4">
              <ChatsByCountriesWidget data={chatsByCountriesData} />
            </div>
          </div>
        );
      })()}
    </DataLoadingWrapper>
  );
};
